// Custom Word Pack creator — user types 5 English words (+ optional VI
// + sentence) and the app builds a playable pack:
//   • Audio: synthesised at runtime via Web Speech API (audioUrl=null in
//     the WordDTO; `speak()` already falls back to the browser).
//   • Images: client-generated coloured initial-letter tiles (data: URLs).
// The new pack is persisted to localStorage and listed alongside the
// built-in packs in the picker.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createCustomPack } from "../local/customPacks";

export function CustomPack() {
  const nav = useNavigate();
  const [title, setTitle] = useState("My Pack");
  const [rows, setRows] = useState<{ en: string; vi: string; sentence: string }[]>(
    Array.from({ length: 5 }, () => ({ en: "", vi: "", sentence: "" })),
  );

  const setRow = (i: number, patch: Partial<{ en: string; vi: string; sentence: string }>) => {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i]!, ...patch };
      return next;
    });
  };

  const sample = () => {
    const sampleRows = [
      { en: "happy", vi: "vui", sentence: "I am happy today." },
      { en: "tired", vi: "mệt", sentence: "I am tired now." },
      { en: "hungry", vi: "đói", sentence: "I am hungry." },
      { en: "thirsty", vi: "khát", sentence: "I am thirsty." },
      { en: "sleepy", vi: "buồn ngủ", sentence: "I am sleepy." },
    ];
    setTitle("Feelings (Sample)");
    setRows(sampleRows);
  };

  const preview = (text: string) => {
    if (!text.trim()) return;
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-US";
    u.rate = 0.9;
    window.speechSynthesis.speak(u);
  };

  const canSave = rows.every((r) => r.en.trim().length > 0);

  const save = () => {
    if (!canSave) return;
    const pack = createCustomPack({
      title,
      words: rows.map((r) => ({
        english: r.en,
        vietnamese: r.vi,
        sentence: r.sentence,
      })),
    });
    // Bounce back to the picker so the new pack is selectable immediately.
    nav(`/play?pack=${pack.id}`, { replace: true });
  };

  return (
    <div className="custom-pack-page">
      <header className="custom-pack-top">
        <button className="picker2-back" onClick={() => nav("/play")} aria-label="Back">←</button>
        <h1 className="custom-pack-title">Create a Custom Pack</h1>
        <button className="picker2-random" onClick={sample}>Use sample</button>
      </header>

      <p className="custom-pack-intro">
        Type 5 English words. The game speaks them aloud with your device's
        voice and makes a friendly tile for each one. Vietnamese + sentence
        are optional but help the lessons feel polished.
      </p>

      <label className="custom-pack-titlerow">
        <span>Pack name</span>
        <input
          className="custom-pack-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={28}
        />
      </label>

      <div className="custom-pack-grid">
        {rows.map((r, i) => (
          <div key={i} className="custom-pack-card">
            <div className="custom-pack-card-num">{i + 1}</div>
            <label>
              <small>English word</small>
              <input
                className="custom-pack-input"
                value={r.en}
                onChange={(e) => setRow(i, { en: e.target.value })}
                placeholder="e.g. happy"
                maxLength={24}
              />
            </label>
            <label>
              <small>Vietnamese (optional)</small>
              <input
                className="custom-pack-input"
                value={r.vi}
                onChange={(e) => setRow(i, { vi: e.target.value })}
                placeholder="e.g. vui"
                maxLength={24}
              />
            </label>
            <label>
              <small>Sentence (optional)</small>
              <input
                className="custom-pack-input"
                value={r.sentence}
                onChange={(e) => setRow(i, { sentence: e.target.value })}
                placeholder="e.g. I am happy today."
                maxLength={80}
              />
            </label>
            <button
              className="custom-pack-listen"
              onClick={() => preview(r.en || "")}
              disabled={!r.en.trim()}
              type="button"
            >
              <span aria-hidden>🔊</span> Listen
            </button>
          </div>
        ))}
      </div>

      <footer className="custom-pack-actions">
        <button className="picker2-flashcards" onClick={() => nav("/play")}>Cancel</button>
        <button className="picker2-play" onClick={save} disabled={!canSave}>
          <span aria-hidden>✨</span> Create Pack
        </button>
      </footer>
    </div>
  );
}
